import React, { useEffect, useState } from "react";
import { useTheme } from "../../contexts/ThemeContext";
import { ReactSVG } from "react-svg";
import apple from "../../assets/apple.svg";
import playStore from "../../assets/google_play_store.svg";

const Top = () => {
    const { colors, isDark, toggleTheme } = useTheme();
    const [isVisible, setIsVisible] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const images = ['sample_1.jpg', 'sample_2.jpg', 'sample_3.jpg', 'sample_4.jpg', 'sample_1.jpg', 'sample_5.jpg', 'sample_6.jpg', 'sample_7.jpg'];

    useEffect(() => {
        setIsVisible(true);
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentImageIndex((prev) => (prev + 1) % images.length);
        }, 5000);
        return () => clearInterval(interval);
    }, [images.length]);

    const nextImage = (e) => {
        e.stopPropagation();
        setCurrentImageIndex((prev) => (prev + 1) % images.length);
    };

    const prevImage = (e) => {
        e.stopPropagation();
        setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
    };

    return (
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
            {/* Navigation - Logo and theme toggle on same line */}
            <nav className="fixed top-0 left-0 right-0 z-50 px-6 py-2.5 backdrop-blur-md border-b border-white/5">
                <div className="max-w-6xl mx-auto flex items-center justify-between h-6">
                    <a href="/" className="flex items-center gap-2.5 h-6 hover:opacity-80 transition-opacity">
                        <img
                            src="/assets/developer_icon_1.jpg"
                            alt="AlienX logo"
                            className="h-6 w-6 rounded-md"
                        />
                        <span className={`text-xl font-semibold leading-none ${colors.text}`}>
                            Alien<span className="blink">X</span>
                        </span>
                    </a>
                    <button
                        onClick={toggleTheme}
                        className={`flex items-center justify-center w-6 h-6 flex-shrink-0 !min-h-0 !min-w-0 p-0 bg-transparent border-0 shadow-none transition-opacity duration-200 hover:opacity-70 ${colors.text}`}
                        aria-label="Toggle theme"
                        style={{ boxShadow: 'none' }}
                    >
                        {isDark ? (
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
                            </svg>
                        ) : (
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                            </svg>
                        )}
                    </button>
                </div>
            </nav>

            {/* Main Hero Content */}
            <div className="relative z-10 w-full max-w-6xl mx-auto px-6 py-20 mt-16">
                <div className="flex flex-col lg:grid lg:grid-cols-2 lg:grid-rows-2 gap-12 lg:gap-16 items-center lg:items-start">
                    
                    {/* 1. Badge + Heading only — mobile: first; lg: top-left */}
                    <div className="order-1 lg:col-start-1 lg:row-start-1 text-center lg:text-left">
                        <div 
                            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8 text-sm font-medium transition-all duration-700 backdrop-blur-md border ${
                                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                            } ${isDark ? 'bg-white/5 border-white/10' : 'bg-black/5 border-black/10'}`}
                        >
                            <span className="relative flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 bg-green-500"></span>
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                            </span>
                            <span className={isDark ? 'text-gray-300' : 'text-gray-600'}>
                                50K+ students trust us daily
                            </span>
                        </div>

                        {/* App Icon + Name */}
                        <div
                            className={`flex items-center gap-3 mb-6 justify-center lg:justify-start transition-all duration-700 delay-75 ${
                                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                            }`}
                        >
                            <img
                                src="/assets/app_logo/SVG/IconForWhiteBackground.svg"
                                alt="Collegia app icon"
                                className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl"
                            />
                            <div>
                                <span className={`text-lg sm:text-xl font-semibold tracking-tight ${colors.text}`}>
                                    Collegia
                                </span>
                                <span className={`block text-xs sm:text-sm font-normal ${colors.textMuted}`}>
                                    Academics Manager
                                </span>
                            </div>
                        </div>

                        <h1
                            className={`heading-xl mb-0 lg:mb-6 transition-all duration-700 delay-100 text-glow ${
                                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                            }`}
                        >
                            <span className={colors.text}>Academic life,</span>
                            <br />
                            <span className="glass-text font-bold">
                                beautifully managed.
                            </span>
                        </h1>
                    </div>

                    {/* 2. App Preview — mobile: between heading and description; lg: right column */}
                    <div
                        className={`order-2 lg:col-start-2 lg:row-start-1 lg:row-span-2 relative flex justify-center lg:items-center transition-all duration-1000 delay-200 ${
                            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                        }`}
                    >
                        <div className="relative float-animation">
                            {/* Device Frame */}
                            <div
                                className={`relative w-[280px] h-[580px] sm:w-[320px] sm:h-[650px] rounded-[3rem] p-3 shadow-2xl overflow-hidden glass-panel`}
                            >
                                {/* Inner Screen Area with Carousel */}
                                <div className={`w-full h-full rounded-[2.25rem] overflow-hidden relative group ${isDark ? 'bg-black' : 'bg-gray-100'}`}>
                                    {/* Carousel Items */}
                                    {images.map((img, index) => (
                                        <div 
                                            key={index}
                                            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                                                index === currentImageIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'
                                            }`}
                                        >
                                            <img
                                                src={`assets/${img}`}
                                                alt={`App Screenshot ${index + 1}`}
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                    ))}

                                    {/* Navigation Arrows */}
                                    <button 
                                        onClick={prevImage}
                                        className="absolute left-2 top-1/2 -translate-y-1/2 z-30 p-2 rounded-full bg-black/30 text-white backdrop-blur-md opacity-0 group-hover:opacity-100 transition-all hover:bg-black/60 hover:scale-110"
                                        aria-label="Previous screenshot"
                                    >
                                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                                        </svg>
                                    </button>
                                    <button 
                                        onClick={nextImage}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 z-30 p-2 rounded-full bg-black/30 text-white backdrop-blur-md opacity-0 group-hover:opacity-100 transition-all hover:bg-black/60 hover:scale-110"
                                        aria-label="Next screenshot"
                                    >
                                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </button>

                                    {/* Glass Reflection overlay */}
                                    <div className="glass-reflection z-20 pointer-events-none"></div>
                                </div>
                            </div>
                            
                            {/* Device Shadow */}
                            <div className="absolute -bottom-10 left-1/2 -translate-x-1/2 w-3/4 h-8 bg-black/40 blur-2xl rounded-full"></div>
                        </div>
                    </div>

                    {/* 3. Description + CTAs + Trust — mobile: after app preview; lg: bottom-left */}
                    <div className="order-3 lg:col-start-1 lg:row-start-2 text-center lg:text-left">
                        {/* Description */}
                        <p 
                            className={`body-lg ${colors.textSecondary} mb-10 max-w-xl mx-auto lg:mx-0 transition-all duration-700 delay-200 ${
                                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                            }`}
                        >
                            Collegia helps university students track attendance, view grades, 
                            manage documents, and stay organized, all in one elegant, privacy-first app.
                        </p>

                        {/* CTA Buttons */}
                        <div 
                            className={`flex flex-col sm:flex-row gap-4 justify-center lg:justify-start transition-all duration-700 delay-300 ${
                                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                            }`}
                        >
                            <a
                                href="https://play.google.com/store/apps/details?id=com.anonymousaliens.cu"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn-primary-minimal"
                            >
                                <ReactSVG src={playStore} className="w-5 h-5 [&_svg]:w-5 [&_svg]:h-5" />
                                <span>Download for Android</span>
                            </a>

                            <a
                                href="https://apps.apple.com/in/app/collegia-academics-manager/id6757691482"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="glass-button"
                            >
                                <ReactSVG src={apple} className={`w-5 h-5 [&_svg]:w-5 [&_svg]:h-5 ${isDark ? '[&_path]:fill-white' : '[&_path]:fill-black'}`} />
                                <span>Download for iOS</span>
                            </a>
                        </div>

                        {/* Trust indicators */}
                        <div 
                            className={`flex items-center gap-6 mt-10 justify-center lg:justify-start transition-all duration-700 delay-400 ${
                                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                            }`}
                        >
                            <div className="flex items-center gap-2">
                                <div className="flex -space-x-1">
                                    {['bg-gray-800', 'bg-gray-700', 'bg-gray-600', 'bg-gray-500'].map((color, i) => (
                                        <div 
                                            key={i}
                                            className={`w-7 h-7 rounded-full ${isDark ? color : 'bg-gray-200'} border-2 ${isDark ? 'border-black' : 'border-white'} flex items-center justify-center text-[10px] font-medium ${isDark ? 'text-white' : 'text-black'}`}
                                        >
                                            {['S', 'A', 'R', 'K'][i]}
                                        </div>
                                    ))}
                                </div>
                                <div className={`text-sm ${colors.textMuted}`}>
                                    <span className={`font-medium ${colors.text}`}>1,500+</span> reviews
                                </div>
                            </div>
                            
                            <div className={`h-4 w-px ${isDark ? 'bg-white/20' : 'bg-black/20'}`} />
                            
                            <div className="flex items-center gap-1.5">
                                <svg className={`w-5 h-5 ${isDark ? 'text-white' : 'text-black'}`} fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
                                </svg>
                                <span className={`font-medium ${colors.text}`}>4.1</span>
                                <span className={`text-sm ${colors.textMuted}`}>rating</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Top;
